import React from "react";
import PropTypes from "prop-types";
import { Dialog, IconButton } from "@mui/material";
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

/**
 * Generic modal shell for small dialogs (filters, quick settings, pickers).
 * Same floating rounded card at every viewport — the AddHubLink "Edit
 * custom link" dialog's chrome (title + optional subtitle, bordered close
 * button, divider, sticky footer) — instead of swapping to a different
 * layout on mobile. Callers only supply title / body / footer actions.
 */
const ResponsiveModal = ({ open, onClose, title = "", subtitle = "", children, actions = null, maxWidth = "xs" }) => (
  <Dialog
    open={open}
    onClose={onClose}
    scroll="paper"
    fullWidth
    maxWidth={maxWidth}
    PaperProps={{
      sx: {
        borderRadius: "16px",
        backgroundColor: "#fff",
        border: "1px solid #EAECF0",
        boxShadow: "0px 8px 8px rgba(16, 24, 40, 0.04), 0px 20px 24px rgba(16, 24, 40, 0.1)",
        maxHeight: "calc(100vh - 32px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      },
    }}
    sx={{
      "& .MuiBackdrop-root": { background: "rgba(16, 24, 40, 0.4)" },
      "& .MuiPaper-root": { margin: { mini: "12px", sm: "24px" } },
    }}
  >
    <SoftBox sx={{ flex: "1 1 auto", overflowY: "auto", px: { mini: 2, sm: 3 }, pt: { mini: 2, sm: 3 }, pb: 2 }}>
      <SoftBox sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 0.5 }}>
        <SoftBox sx={{ minWidth: 0 }}>
          <SoftTypography sx={{ fontFamily: "DM Sans", fontSize: "17px!important", fontWeight: "700!important", color: "#101828" }} component="h2">
            {title}
          </SoftTypography>
          {subtitle && (
            <SoftTypography variant="caption" sx={{ color: "#667085", display: "block", mt: 0.5 }}>
              {subtitle}
            </SoftTypography>
          )}
        </SoftBox>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{ color: "#667085", border: "1px solid #D0D5DD", flexShrink: 0 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </SoftBox>

      <SoftBox sx={{ borderTop: "1px solid #EAECF0", my: 2 }} />

      {children}
    </SoftBox>

    {actions && (
      <SoftBox
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          px: { mini: 2, sm: 3 },
          py: 2,
          borderTop: "1px solid #EAECF0",
          backgroundColor: "#fff",
        }}
      >
        {actions}
      </SoftBox>
    )}
  </Dialog>
);

ResponsiveModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  title: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  // eslint-disable-next-line react/require-default-props
  actions: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
};

export default ResponsiveModal;
